'use client';

import { useCouponPreviewCoupon } from '@ps/features/coupons/contexts/preview/context';
import { couponPreviewPrice } from '@ps/features/coupons/functions/couponPreviewPrice';
import { useIsAppUser } from '@ps/functions/hooks/useIsAppUser';
import { type WithID } from '@ps/models/lib/base';
import { type Listing } from '@ps/models/lib/listing';
import currency from 'currency.js';

import { ExpressApplePayGate } from '../../checkout/components/express-apple-pay/ExpressApplePayGate';
import type { CheckoutIntent } from '../../checkout/types/CheckoutIntent';
import { usePdpPurchase } from '../contexts/purchase/context';
import { useVariantQuantity } from '../contexts/variant-quantity/context';
import {
  PURCHASED_DIGITAL_CTA_LABEL,
  getListingPriceInfo,
  getPrimaryButtonTitle,
  isExternalListing,
} from '../functions/listingDisplay';
import type { ProductAccessGuard } from '../functions/resolveProductAccessGuard';
import { BuyNowButton } from './BuyNowButton';
import { CtaFooter } from './CtaFooter';
import { NonPurchasableActions } from './NonPurchasableActions';

type BuyNowActionProps = {
  listing: WithID<Listing>;
  /**
   * Purchase/download access requirement (resolved server-side). Used only to
   * decide whether the express Apple Pay button may render (ungated native
   * path); the actual gating lives in `PdpPurchaseProvider`.
   */
  accessGuard?: ProductAccessGuard;
  /**
   * Server-resolved base checkout intent for the express "Buy with Apple Pay"
   * button (physical / digital, non-external). `null` when the listing isn't
   * bought this way. Only rendered on the UNGATED path — a gated listing must
   * clear its follow / subscription gate through the normal flow first.
   */
  productIntent?: CheckoutIntent | null;
};

/**
 * The sticky-bottom primary action. A thin consumer of the shared
 * {@link PdpPurchaseProvider}: it reads `onPurchase` (identical to the inline
 * digital-summary CTA) and renders the label variant-/coupon-aware. Falls back
 * to RN's "Explore Other Items" (`NonPurchasableActions`) when the listing is
 * non-purchasable (the provider then supplies no purchase context).
 */
export function BuyNowAction({
  listing,
  accessGuard,
  productIntent,
}: BuyNowActionProps) {
  const ctx = useVariantQuantity();
  const purchase = usePdpPurchase();
  const couponPreview = useCouponPreviewCoupon();
  const isAppUser = useIsAppUser();

  // No purchase context → non-purchasable listing (RN's "Explore Other Items").
  if (!purchase) return <NonPurchasableActions />;

  // External / affiliate listings check out on a third-party URL, which an app
  // user must not be offered at all (store compliance) — the tap can only ever
  // reach `AppUserPurchaseAlert`, so render no CTA instead of a dead button.
  // `useIsAppUser` is false on the server AND the first hydration render, so
  // SSR output is unchanged; the CTA disappears right after hydration. The
  // sticky wrapper in `ProductDetail` collapses (`empty:hidden`) once this and
  // `CtaFooter` render nothing.
  if (isExternalListing(listing) && isAppUser) return null;

  if (purchase.isPurchased) {
    return (
      <BuyNowButton
        label={PURCHASED_DIGITAL_CTA_LABEL}
        onClick={purchase.onPurchase}
        disabled={false}
      />
    );
  }

  const price = getListingPriceInfo(listing, ctx?.selectedVariant);
  // Reflect the coupon-preview discount in the CTA price too (RN parity), so
  // "Download Now for $X" matches the strikethrough price cell.
  const couponDiscounted = couponPreviewPrice(
    { listPrice: price.price, currentPrice: price.effectivePrice },
    couponPreview,
  );
  const ctaPrice = couponDiscounted ?? price.effectivePrice;
  const label = getPrimaryButtonTitle(
    listing,
    price.isFree,
    currency(ctaPrice).format(),
  );

  const external = isExternalListing(listing);
  const gated = !external && accessGuard != null && accessGuard.kind !== 'none';
  // Apple Pay has nothing to authorize at $0 — Stripe rejects a zero amount, so
  // the sheet either fails or charges nothing, and "Buy with Apple Pay" under a
  // FREE product reads as a price the page has not disclosed. Keyed on the CTA
  // price, not `price.isFree`, so a coupon that takes the total to zero drops the
  // button too. The free path is the "Download Now" CTA above.
  const freeCheckout = currency(ctaPrice).value <= 0;

  // The express Apple Pay button completes the purchase in place, so it's only
  // for the ungated native path (a gated listing must clear its follow /
  // subscription gate first). It keeps the engine's variant + quantity in sync
  // with the on-page selection (the digital engine ignores `variantchange`).
  const expressButton =
    productIntent && !external && !gated && !freeCheckout ? (
      <ExpressApplePayGate
        intent={productIntent}
        borderRadius={8}
        syncEngine={engine => {
          const variantId = ctx?.selectedVariant?.id;
          if (variantId) {
            engine.gossip('variantchange', variantId);
          }
          engine.gossip('quantitychange', ctx?.quantity ?? 1);
        }}
      />
    ) : null;

  return (
    <>
      <BuyNowButton
        label={label}
        onClick={purchase.onPurchase}
        disabled={!purchase.isReady}
      />
      {expressButton && <div className="mt-2">{expressButton}</div>}
      <CtaFooter showLogin={!external} />
    </>
  );
}
